
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Upload, X, Image } from "lucide-react";
import { uploadCourseImage, removeCourseImage } from "@/services/courseManagementService";

interface CourseImageUploadProps {
  courseId: string;
  currentImageUrl: string | null;
  onImageUpdate: (newUrl: string | null) => void;
}

// List of allowed MIME types
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
// Maximum file size in bytes (2MB)
const MAX_SIZE = 2 * 1024 * 1024;

const CourseImageUpload: React.FC<CourseImageUploadProps> = ({
  courseId,
  currentImageUrl,
  onImageUpdate
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  
  // Handle file upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Security check: Validate file type 
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a JPEG, PNG, WEBP or GIF image.",
      });
      return;
    }
    
    // Security check: Validate file size
    if (file.size > MAX_SIZE) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Image must be less than 2MB.",
      });
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Security check: Add random suffix to filename to prevent path traversal
      const fileExtension = file.name.split('.').pop();
      const randomSuffix = Math.random().toString(36).substring(2, 15);
      const secureFilename = `${randomSuffix}.${fileExtension}`;
      
      // Create a new file object with the secure name
      const secureFile = new File([file], secureFilename, { type: file.type });
      
      const imageUrl = await uploadCourseImage(courseId, secureFile);
      
      toast({
        title: "Success",
        description: "Course image uploaded successfully.",
      });
      
      // Update parent component
      onImageUpdate(imageUrl);
      
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "Failed to upload image. Please try again.",
      });
    } finally {
      setIsUploading(false);
      // Reset the file input
      e.target.value = '';
    }
  };
  
  // Remove the current image
  const handleRemoveImage = async () => {
    if (!currentImageUrl) return;
    
    try {
      setIsRemoving(true);
      
      await removeCourseImage(courseId);
      
      toast({
        title: "Success",
        description: "Course image removed successfully.",
      });
      
      // Update parent component
      onImageUpdate(null);
      
    } catch (error) {
      console.error("Error removing image:", error);
      toast({
        variant: "destructive",
        title: "Removal failed",
        description: "Failed to remove image. Please try again.",
      });
    } finally {
      setIsRemoving(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center">
        {currentImageUrl ? (
          <div className="relative w-full max-w-md">
            <img 
              src={currentImageUrl} 
              alt="Course cover" 
              className="w-full rounded-md border border-gray-200 object-cover h-48"
              onError={(e) => {
                // Handle image loading errors
                e.currentTarget.src = "/placeholder.svg";
                toast({
                  variant: "destructive",
                  title: "Image error",
                  description: "Could not load image. A placeholder is shown instead.",
                });
              }}
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8 rounded-full"
              onClick={handleRemoveImage}
              disabled={isRemoving || isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="w-full max-w-md h-48 flex items-center justify-center border border-dashed border-gray-300 rounded-md bg-gray-50">
            <div className="flex flex-col items-center space-y-2 text-gray-500">
              <Image className="h-10 w-10" />
              <span>No image uploaded</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex items-center">
        <label htmlFor="image-upload" className="cursor-pointer">
          <Button 
            type="button"
            variant={currentImageUrl ? "outline" : "default"}
            className="relative"
            disabled={isUploading || isRemoving}
          >
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? "Uploading..." : currentImageUrl ? "Change Image" : "Upload Image"}
            <input
              id="image-upload"
              type="file"
              accept="image/jpeg, image/png, image/webp, image/gif"
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
              onChange={handleFileChange}
              disabled={isUploading || isRemoving}
              aria-label="Upload course image"
            />
          </Button>
        </label>
        
        {currentImageUrl && (
          <Button
            type="button"
            variant="outline"
            className="ml-2"
            onClick={handleRemoveImage}
            disabled={isRemoving || isUploading}
          >
            <X className="mr-2 h-4 w-4" />
            {isRemoving ? "Removing..." : "Remove Image"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CourseImageUpload;
